import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Fingerprint, Monitor, Smartphone, Key, Plus, Trash2, Pencil, Check, X, RefreshCw } from "lucide-react";
import apiClient from "../../services/api";
import { registerPasskey, passkeyErrorMessage, formatPasskeyDate } from "../../lib/passkeys";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

const STALE_DAYS = 90;
const isStale = (pk) => {
  const ref = pk.last_used || pk.created_at;
  return ref ? (Date.now() - new Date(ref).getTime()) / 86400000 > STALE_DAYS : false;
};

const DeviceIcon = ({ pk }) => {
  const n = (pk.device_name || "").toLowerCase();
  if (/iphone|ipad|android|phone/.test(n)) return <Smartphone className="w-5 h-5 text-green-600" />;
  if (/windows|mac|pc/.test(n)) return <Monitor className="w-5 h-5 text-blue-600" />;
  if (pk.device_type === "cross-platform") return <Key className="w-5 h-5 text-amber-600" />;
  return <Fingerprint className="w-5 h-5 text-purple-600" />;
};

const PasskeyRow = ({ pk, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(pk.device_name);
  const save = async () => { await onRename(pk.id, name); setEditing(false); };
  const stale = isStale(pk);
  return (
    <div className={`flex items-center gap-3 rounded-lg border p-3 ${stale ? "border-amber-300 bg-amber-50/60" : "border-border bg-muted/30"}`} data-testid={`passkey-device-${pk.id}`}>
      <DeviceIcon pk={pk} />
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="h-8" autoFocus onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} data-testid={`passkey-rename-input-${pk.id}`} />
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={save} data-testid={`passkey-rename-save-${pk.id}`}><Check className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setName(pk.device_name); setEditing(false); }}><X className="w-4 h-4" /></Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="font-medium truncate" data-testid={`passkey-device-name-${pk.id}`}>{pk.device_name}</p>
            <button type="button" className="text-muted-foreground hover:text-foreground" title="Rename" onClick={() => setEditing(true)} data-testid={`passkey-rename-${pk.id}`}><Pencil className="w-3.5 h-3.5" /></button>
            {stale && <span className="text-xs text-amber-800 bg-amber-100 rounded px-1.5 py-0.5" data-testid={`passkey-stale-${pk.id}`}>Not used in {STALE_DAYS}+ days</span>}
          </div>
        )}
        <p className="text-xs text-muted-foreground" data-testid={`passkey-dates-${pk.id}`}>
          Added {formatPasskeyDate(pk.created_at)} · Last used {formatPasskeyDate(pk.last_used)}
        </p>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" title="Remove passkey" data-testid={`passkey-remove-${pk.id}`}><Trash2 className="w-4 h-4" /></Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{pk.device_name}"?</AlertDialogTitle>
            <AlertDialogDescription>That device won't be able to sign in with this passkey anymore. You can add a new one from the device at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(pk.id)} className="bg-red-600 hover:bg-red-700" data-testid={`passkey-remove-confirm-${pk.id}`}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Profile card: every saved passkey with device name + last-used date, rename, remove, add this device.
const PasskeyDevicesCard = () => {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const load = useCallback(async () => {
    try { setPasskeys((await apiClient.get(`/passkeys/list`)).data); }
    catch { toast.error("Failed to load passkeys"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setRegistering(true);
    try { await registerPasskey(); toast.success("Passkey added for this device"); load(); }
    catch (err) { toast.error(passkeyErrorMessage(err)); }
    finally { setRegistering(false); }
  };
  const rename = async (id, name) => {
    try { await apiClient.patch(`/passkeys/${encodeURIComponent(id)}`, { name }); toast.success("Passkey renamed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not rename"); }
  };
  const remove = async (id) => {
    try { await apiClient.delete(`/passkeys/${encodeURIComponent(id)}`); toast.success("Passkey removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };

  return (
    <Card data-testid="passkey-devices-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2"><Fingerprint className="w-5 h-5" /> Passkeys</CardTitle>
          <CardDescription>Devices that can sign in without a password. Remove any you no longer use.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={load} title="Refresh" data-testid="passkey-devices-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={add} disabled={registering} data-testid="passkey-add-device-btn">
            <Plus className="w-4 h-4 mr-2" /> {registering ? "Waiting…" : "Add this device"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
          : passkeys.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="passkey-devices-empty">No passkeys yet. Add one to sign in with Windows Hello, Face ID or Touch ID.</p>
          : passkeys.map((pk) => <PasskeyRow key={pk.id} pk={pk} onRename={rename} onDelete={remove} />)}
      </CardContent>
    </Card>
  );
};

export default PasskeyDevicesCard;
